import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  Input,
  Output,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ValidationErrorService } from 'src/app/core/services/error/validation-error/validation-error.service';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Represents a select field
 */
export class SelectComponent implements OnInit, OnDestroy {
  @Input() label?: string;
  @Input() placeholder!: string;
  @Input() control!: FormControl;
  @Input() options?: any[];
  @Input() value?: any;
  @Input() disabled: boolean = false;
  @Input() optionAccessor?: (arg0: any) => string;
  @Output() selectionChange = new EventEmitter<string>();
  panelIsOpen: boolean = false;
  private _v2: boolean = false;
  hideLabel: boolean = false;
  @Input() isMultiSelect = false;

  private _required: boolean = false;
  private _isSmall = false;
  private subsink = new Subscription();
  errorMessage = '';

  @ViewChild('select') select!: ElementRef;

  /**
   * @constructor
   */
  constructor(private validationErrorService: ValidationErrorService) {}

  /**
   * on init
   */
  ngOnInit() {
    this.setErrorMessage(this.control);
    const subscription = this.control.statusChanges.subscribe(() => {
      this.setErrorMessage(this.control);
    });
    this.subsink.add(subscription);

    if (this.label == undefined) {
      this.hideLabel = true;
    }
  }

  /**
   * clean up subscriptions
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * If this select is required or not
   * @return {boolean}
   */
  @Input()
  get required(): boolean {
    return this._required;
  }

  /**
   * Sets this select as required
   * @param {any} value
   */
  set required(value: any) {
    this._required = coerceBooleanProperty(value);
  }

  /**
   * Sets error message of this input
   * @param {AbstractControl} abstractControl
   */
  setErrorMessage(abstractControl: AbstractControl) {
    this.validationErrorService
      .translateError(abstractControl)
      .then((errorMessage) => {
        this.errorMessage = errorMessage;
      });
  }

  /**
   * Controls icon change after click in select
   */
  handleOpenChange() {
    for (const el of Array.from(document.getElementsByTagName('mat-select'))) {
      (el as HTMLElement).blur();
    }

    this.panelIsOpen = !this.panelIsOpen;
  }

  /**
   * Decides if this height is smaller
   * @return {boolean}
   */
  @Input()
  get small(): boolean {
    return this._isSmall;
  }

  /**
   * Sets this height as a smaller one by using an html attribute
   * @param {any} value
   */
  set small(value: any) {
    this._isSmall = coerceBooleanProperty(value);
  }

  /**
   * If an option accessor is given, it uses it to get a string value
   * from an option. This allows option objects to be used.
   * @param {any} option
   * @return {string}
   */
  getOption(option: any): string {
    if (option) {
      return this.optionAccessor ? this.optionAccessor(option) : option;
    } else return '';
  }

  /**
   * Controls icon change after click in select
   */
  changeSelect() {
    this.selectionChange.emit();
  }

  /**
   * If two options accessors are given, it changes the first string value to the second.
   * @param {any} option1
   * @param {any} option2
   * @return {any}
   */
  optionCompareFn = (option1: any, option2: any) =>
    this.getOption(option1) === this.getOption(option2);

  /**
   * Whether is V2 or not
   */
  @Input()
  get v2(): boolean {
    return this._v2;
  }

  /**
   * Sets as v2
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set v2(value: any) {
    this._v2 = coerceBooleanProperty(value);
  }
}
